import { useContext, useEffect, useRef } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Sparkles, RotateCcw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLabels } from '../config/useLabels';
import { useBrandProfile } from '../contexts/brand-profile';
import { brandProfileOf } from '../config/brandProfiles';
import { GenieChatContext, useGenieChat } from '../contexts/genie-chat';
import { GenieChatProvider } from '../contexts/GenieChatContext';
import { analytics } from '../utils/analytics';
import { GenieConversation } from '../components/genie/GenieConversation';

function ParentGeniePage() {
  const { user, isLoading } = useAuth();
  const t = useLabels();
  const { activeBrand } = useBrandProfile();
  const { messages, loading, clearThread } = useGenieChat();
  const [searchParams] = useSearchParams();
  const viewTrackedRef = useRef(false);

  // Log a landing event the first time the assistant is shown to a signed-in parent —
  // captures that they opened it even if they never send a question. `source` (e.g.
  // 'flex_plan') marks where the quick link came from.
  useEffect(() => {
    if (!user || viewTrackedRef.current) return;
    viewTrackedRef.current = true;
    analytics.trackAgentView({ user, source: searchParams.get('source') ?? undefined });
  }, [user, searchParams]);

  if (!isLoading && !user) return <Navigate to="/login" replace />;

  const isEmpty = messages.length === 0;

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 flex flex-col" style={{ minHeight: 'calc(100vh - 8rem)' }}>
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="h-6 w-6 text-blue-600" />
        <h1 className="text-xl font-semibold text-gray-900">{t('genie.title')}</h1>
        {!isEmpty && (
          <button
            type="button"
            onClick={clearThread}
            disabled={loading}
            className="ml-auto flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 disabled:opacity-50"
          >
            <RotateCcw className="h-3.5 w-3.5" /> {t('genie.newChat')}
          </button>
        )}
      </div>
      {/*
        "Ask in English or Chinese." is an offer, and an English-only brand should not make it.
        Gated on the BRAND's own language. Nothing about the assistant's capability changes
        here; it is the advertisement that is brand-specific.
      */}
      {brandProfileOf(activeBrand).bilingual && (
        <p className="text-sm text-gray-500 mb-4">{t('genie.subtitle')}</p>
      )}

      {/* Prefill from a `?q=` deep link (e.g. the flex-planner "ask the assistant" link) so the
          parent can just hit send. We don't auto-submit. */}
      <GenieConversation variant="page" prefill={searchParams.get('q') ?? undefined} />
    </div>
  );
}

/**
 * The full-page Parent Genie. The conversation normally comes from the GenieChatProvider in
 * the parent Layout, shared with the Assistant bubble; rendered without one (a test, or any
 * route outside Layout) it brings its own.
 */
export default function ParentGenie() {
  const shared = useContext(GenieChatContext);
  if (shared) return <ParentGeniePage />;
  return (
    <GenieChatProvider>
      <ParentGeniePage />
    </GenieChatProvider>
  );
}
